/**
 * Sign-in, for both portals.
 *
 * The two approved login designs are the same layout with different content:
 * the family one adds a "Parent / Student Portal" badge, different copy, a
 * different headline and its own feature list. So this is one component
 * parameterised by portal rather than two that will drift apart.
 *
 * Two states here are not errors and are rendered as ordinary steps:
 *
 *   - **Choose an institution.** One address can belong to several schools —
 *     a parent with children in two districts. The server asks before
 *     checking the password, so which schools exist for an address does not
 *     depend on getting the password right.
 *   - **Wrong portal.** A teacher who lands on the family site is told where
 *     to go rather than being shown "incorrect password" for a password that
 *     is in fact correct.
 */

import { useId, useState, type FormEvent } from 'react'
import { api, currentPortal, otherPortalUrl, setLocalPortal, ApiError, isTransientFailure, retryWhileRestarting, unreachableMessage, type InstitutionChoice, type Portal } from '@/lib/api'
import { useSession } from '@/lib/session'
import { SchoolMark } from '@/components/SchoolMark'
import './login.css'

interface PortalCopy {
  badge: string | null
  subtitle: string
  eyebrow: string
  headline: string
  blurb: string
  points: { icon: PointIcon; title: string; body: string }[]
  /** The label on the control that sends someone to the other portal. */
  crossPortal: string
}

type PointIcon = 'shield' | 'chart' | 'calendar' | 'document' | 'cap' | 'people' | 'book'

const COPY: Record<Portal, PortalCopy> = {
  staff: {
    badge: null,
    subtitle: 'Sign in to your staff and administrator account to continue to Cornerstone Education.',
    eyebrow: 'A stronger school community together',
    headline: 'Tools for today.\nBrighter futures tomorrow.',
    blurb:
      'Cornerstone Education helps staff and administrators manage students, support learning, and keep school communities connected — all in one secure platform.',
    points: [
      { icon: 'shield', title: 'Secure access', body: 'Keep student data safe with role-based permissions.' },
      { icon: 'chart', title: 'Grades & academic tools', body: 'Track progress and support student achievement.' },
      { icon: 'calendar', title: 'Attendance & scheduling', body: 'Stay organized and informed every day.' },
      { icon: 'document', title: 'Reports & student services', body: 'Turn data into meaningful action for your school community.' },
    ],
    crossPortal: 'Access Student/Parent Portal',
  },
  family: {
    badge: 'Parent / Student Portal',
    subtitle: 'Sign in to your parent or student account to stay connected with your school.',
    eyebrow: 'A stronger school community together',
    headline: 'Students grow.\nFamilies stay connected.',
    blurb:
      'Stay up to date with grades, assignments, attendance, announcements and more — all in one place.',
    points: [
      { icon: 'cap', title: 'Track progress', body: 'View grades, assignments and academic growth.' },
      { icon: 'people', title: 'Stay informed', body: 'See announcements and important school updates.' },
      { icon: 'calendar', title: 'Manage attendance', body: 'Keep track of schedules and attendance records.' },
      { icon: 'book', title: 'Support success', body: 'Partner together for a brighter future.' },
    ],
    crossPortal: 'Access Staff Portal',
  },
}

export function LoginPage() {
  const portal = currentPortal()
  const copy = COPY[portal]
  const { signIn } = useSession()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState<{ tone: 'error' | 'info'; text: string } | null>(null)
  const [choices, setChoices] = useState<InstitutionChoice[] | null>(null)
  const [mfaStep, setMfaStep] = useState(false)
  const [mfaCode, setMfaCode] = useState('')

  const emailId = useId()
  const passwordId = useId()
  const errorId = useId()
  const crossPortalHref = otherPortalUrl()

  async function attempt(tenantId?: string) {
    setSubmitting(true)
    setMessage(null)
    try {
      // During an update the server is away for a minute; wait it out instead of reporting a dead connection.
      const result = await retryWhileRestarting(() => api.login({ email, password, rememberMe, tenantId, ...(mfaStep && mfaCode ? { mfaCode: mfaCode.trim() } : {}) }), { onWaiting: () => setMessage({ tone: 'info', text: 'Cornerstone is updating — one moment…' }) })

      if (result.kind === 'choose_institution') {
        setChoices(result.options)
        return
      }

      if (result.kind === 'mfa_required') {
        setMfaStep(true)
        if (result.invalid) setMessage({ tone: 'error', text: result.message })
        else setMessage({ tone: 'info', text: result.message })
        return
      }

      if (result.kind === 'wrong_portal') {
        setMessage({ tone: 'info', text: result.message })
        return
      }

      signIn(result.session)
    } catch (caught) {
      setMessage({
        tone: 'error',
        text:
          caught instanceof ApiError && !isTransientFailure(caught)
            ? caught.message
            : unreachableMessage(),
      })
    } finally {
      setSubmitting(false)
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    void attempt()
  }

  return (
    <div className="login" data-portal={portal}>
      <main className="login__panel" aria-labelledby="login-heading">
        <header className="login__brand">
          <SchoolMark className="login__mark" />
          <div className="login__wordmark">
            <span className="login__wordmark-name">
              <strong>Cornerstone</strong> Education
            </span>
            <span className="login__wordmark-tagline">People · Progress · Brighter Futures</span>
          </div>
        </header>

        <div className="login__body">
          {copy.badge && (
            <p className="login__badge">
              <PointGlyph icon="people" />
              {copy.badge}
            </p>
          )}

          <h1 className="login__heading" id="login-heading">
            Welcome back
          </h1>
          <p className="login__subtitle">{copy.subtitle}</p>

          {choices ? (
            <InstitutionChooser
              choices={choices}
              submitting={submitting}
              onChoose={(tenantId) => void attempt(tenantId)}
              onBack={() => setChoices(null)}
            />
          ) : (
            <form className="login__form" onSubmit={onSubmit} noValidate>
              {message && (
                <p
                  className={`login__message login__message--${message.tone}`}
                  id={errorId}
                  role={message.tone === 'error' ? 'alert' : 'status'}
                >
                  {message.text}
                </p>
              )}

              <div className="field">
                <label className="field__label" htmlFor={emailId}>
                  Email or Username
                </label>
                <div className="field__control">
                  <MailGlyph />
                  <input
                    id={emailId}
                    type="email"
                    name="email"
                    autoComplete="username"
                    required
                    placeholder="name@cornerstone.edu or username"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    aria-describedby={message?.tone === 'error' ? errorId : undefined}
                    aria-invalid={message?.tone === 'error' || undefined}
                  />
                </div>
              </div>

              <div className="field">
                <label className="field__label" htmlFor={passwordId}>
                  Password
                </label>
                <div className="field__control">
                  <LockGlyph />
                  <input
                    id={passwordId}
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    autoComplete="current-password"
                    required
                    placeholder="Enter your password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    aria-describedby={message?.tone === 'error' ? errorId : undefined}
                  />
                  <button
                    type="button"
                    className="field__reveal"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-pressed={showPassword}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <EyeGlyph off={!showPassword} />
                  </button>
                </div>
              </div>

              {mfaStep && (
                <div className="field">
                  <label className="field__label" htmlFor={`${passwordId}-mfa`}>
                    Verification code
                  </label>
                  <div className="field__control">
                    <LockGlyph />
                    <input id={`${passwordId}-mfa`} inputMode="numeric" autoComplete="one-time-code" autoFocus placeholder="6-digit code, or a recovery code" value={mfaCode} onChange={(event) => setMfaCode(event.target.value)} />
                  </div>
                </div>
              )}

              <div className="login__row">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(event) => setRememberMe(event.target.checked)}
                  />
                  <span className="checkbox__box" aria-hidden="true">
                    <CheckGlyph />
                  </span>
                  Remember me
                </label>
                <a className="login__forgot" href="/forgot-password">
                  Forgot password?
                </a>
              </div>

              <button className="button button--primary button--block" type="submit" disabled={submitting}>
                {submitting ? 'Signing in…' : 'Sign In'}
                {!submitting && <ArrowGlyph />}
              </button>

              <div className="login__divider">
                <span>OR</span>
              </div>

              {/*
                Single sign-on is a Pro entitlement and is not wired yet. It is
                shown disabled with the reason rather than hidden, because the
                approved design includes it and a button that silently does
                nothing is worse than one that says why.
              */}
              <button
                className="button button--secondary button--block"
                type="button"
                disabled
                title="Single sign-on is configured per school. Contact your administrator."
              >
                <MicrosoftGlyph />
                Sign in with Microsoft
              </button>

              {/*
                The way to the other portal.
                
                A real link in production, because the portals are separate
                hostnames and each refuses the other's accounts — a parent who
                lands here has nowhere to go otherwise, and the wrong-portal
                message names the right site without being able to take them
                to it. Where the hostname cannot express the split (localhost,
                the generic azurestaticapps.net address) it falls back to the
                stored preference, so the control works everywhere rather than
                being a link to the page you are already on.
              */}
              {crossPortalHref ? (
                <a className="button button--ghost button--block login__cross-portal" href={crossPortalHref}>
                  {copy.crossPortal}
                  <ArrowGlyph />
                </a>
              ) : (
                <button
                  className="button button--ghost button--block login__cross-portal"
                  type="button"
                  onClick={() => {
                    setLocalPortal(portal === 'staff' ? 'family' : 'staff')
                    window.location.reload()
                  }}
                >
                  {copy.crossPortal}
                  <ArrowGlyph />
                </button>
              )}
            </form>
          )}
        </div>

        {/* The Cornerstone logo, dark variant for the light panel. Dropped at
            web/public/logo/cornerstone_logo_light.svg (black artwork); until the file exists the broken-image
            handler removes the slot entirely. */}
        <img
          className="login__logo"
          src="/logo/cornerstone_logo_light.svg"
          alt="Cornerstone"
          onError={(event) => {
            event.currentTarget.style.display = 'none'
          }}
        />

        <footer className="login__footer">
          <span>Environment: {import.meta.env.MODE === 'production' ? 'Production' : 'Development'}</span>
          <span aria-hidden="true">|</span>
          <span>Version {__APP_VERSION__}</span>
        </footer>
      </main>

      <aside className="login__aside" aria-hidden="true">
        <div className="login__aside-inner">
          <p className="login__eyebrow">{copy.eyebrow}</p>
          <h2 className="login__headline">
            {copy.headline.split('\n').map((line) => (
              <span key={line}>{line}</span>
            ))}
          </h2>
          <p className="login__blurb">{copy.blurb}</p>
          <ul className="login__points">
            {copy.points.map((point) => (
              <li key={point.title}>
                <span className="login__point-icon">
                  <PointGlyph icon={point.icon} />
                </span>
                <span>
                  <strong>{point.title}</strong>
                  {point.body}
                </span>
              </li>
            ))}
          </ul>

          {/* Light variant for the dark panel: web/public/logo/cornerstone_logo_dark.svg (light-grey artwork). */}
          <img
            className="login__logo login__logo--aside"
            src="/logo/cornerstone_logo_dark.svg"
            alt=""
            onError={(event) => {
              event.currentTarget.style.display = 'none'
            }}
          />
        </div>
      </aside>
    </div>
  )
}

function InstitutionChooser({
  choices,
  submitting,
  onChoose,
  onBack,
}: {
  choices: InstitutionChoice[]
  submitting: boolean
  onChoose: (tenantId: string) => void
  onBack: () => void
}) {
  return (
    <div className="chooser">
      <p className="chooser__intro">
        That email is registered at more than one school. Which one are you signing in to?
      </p>
      <ul className="chooser__list">
        {choices.map((choice) => (
          <li key={choice.tenantId}>
            <button
              type="button"
              className="chooser__option"
              disabled={submitting}
              onClick={() => onChoose(choice.tenantId)}
            >
              <span>{choice.institutionName}</span>
              <ArrowGlyph />
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className="button button--ghost" onClick={onBack} disabled={submitting}>
        Back
      </button>
    </div>
  )
}

/* ── Inline glyphs ──────────────────────────────────────────────────────────
 * Drawn inline rather than pulled from an icon package: the set is small and
 * fixed, and an icon font or component library would be a dependency, a
 * network request and a flash of unstyled content on the one screen that has
 * to feel instant.
 */

function MailGlyph() {
  return (
    <svg className="glyph" viewBox="0 0 20 20" aria-hidden="true">
      <rect x="2.5" y="4.5" width="15" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M3 6l7 5 7-5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

function LockGlyph() {
  return (
    <svg className="glyph" viewBox="0 0 20 20" aria-hidden="true">
      <rect x="4" y="8.5" width="12" height="8" rx="2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M7 8.5V6.8a3 3 0 016 0v1.7" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  )
}

function EyeGlyph({ off }: { off: boolean }) {
  return (
    <svg className="glyph" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M1.8 10S5 4.8 10 4.8 18.2 10 18.2 10 15 15.2 10 15.2 1.8 10 1.8 10z" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="10" cy="10" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.4" />
      {off && <path d="M3.5 3.5l13 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />}
    </svg>
  )
}

function CheckGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ArrowGlyph() {
  return (
    <svg className="glyph" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M4 10h11m-4.5-4.5L15 10l-4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function MicrosoftGlyph() {
  return (
    <svg className="glyph" viewBox="0 0 20 20" aria-hidden="true">
      <rect x="2" y="2" width="7.6" height="7.6" fill="#F25022" />
      <rect x="10.4" y="2" width="7.6" height="7.6" fill="#7FBA00" />
      <rect x="2" y="10.4" width="7.6" height="7.6" fill="#00A4EF" />
      <rect x="10.4" y="10.4" width="7.6" height="7.6" fill="#FFB900" />
    </svg>
  )
}

function PointGlyph({ icon }: { icon: PointIcon }) {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
  switch (icon) {
    case 'shield':
      return (
        <svg className="glyph" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3l7 3v5.5c0 4.2-2.9 7.9-7 9.5-4.1-1.6-7-5.3-7-9.5V6z" {...common} />
          <path d="M9 12l2 2 4-4" {...common} />
        </svg>
      )
    case 'chart':
      return (
        <svg className="glyph" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 19V11M12 19V5M19 19v-5" {...common} strokeWidth={2} />
        </svg>
      )
    case 'calendar':
      return (
        <svg className="glyph" viewBox="0 0 24 24" aria-hidden="true">
          <rect x="4" y="5.5" width="16" height="14" rx="2" {...common} />
          <path d="M4 10h16M9 3.5v4M15 3.5v4" {...common} />
        </svg>
      )
    case 'document':
      return (
        <svg className="glyph" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" {...common} />
          <path d="M14 3v5h5M9 13h6M9 17h4" {...common} />
        </svg>
      )
    case 'cap':
      return (
        <svg className="glyph" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 5l9 4-9 4-9-4z" {...common} />
          <path d="M7 11.2V16c0 1.4 2.2 2.5 5 2.5s5-1.1 5-2.5v-4.8" {...common} />
        </svg>
      )
    case 'people':
      return (
        <svg className="glyph" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="9" cy="9" r="3" {...common} />
          <path d="M3.5 19a5.5 5.5 0 0111 0M16 7.2a3 3 0 010 5.6M17.5 19a5.6 5.6 0 00-2-4.1" {...common} />
        </svg>
      )
    case 'book':
      return (
        <svg className="glyph" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 5.5A1.5 1.5 0 015.5 4H11v16H5.5A1.5 1.5 0 014 18.5z" {...common} />
          <path d="M20 5.5A1.5 1.5 0 0018.5 4H13v16h5.5a1.5 1.5 0 001.5-1.5z" {...common} />
        </svg>
      )
  }
}
